import Seo from "@/components/layout/Seo";
import Hero from "@/components/marketing/Hero";
import ServicesGrid from "@/components/marketing/ServicesGrid";
import ContactForm from "@/components/marketing/ContactForm";
import SectionCTA from "@/components/marketing/SectionCTA";
import InlineCTA from "@/components/marketing/InlineCTA";
import Reveal from "@/components/layout/Reveal";
import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, Clock, MapPin, Award, Handshake, Wrench } from "lucide-react";
import { PROJECTS, REASSURANCE } from "@/lib/site";

export default function HomePage() {
    return (
        <>
            <Seo
                title="Terrassement, démolition et génie civil à Genève"
                description="RCPS Terrassement Sàrl réalise vos travaux de terrassement, démolition, génie civil, voirie, enrobés, aménagements extérieurs et enrochement à Genève et dans toute la Suisse romande."
            />
            <Hero />

            {/* Intro */}
            <section className="bg-white">
                <div className="rcps-container py-20 md:py-28 grid md:grid-cols-12 gap-10 items-start">
                    <div className="md:col-span-5">
                        <Reveal>
                            <p className="kicker text-navy-700">
                                <span className="hairline" />
                                L’entreprise
                            </p>
                            <h2 className="mt-4 font-heading text-3xl md:text-5xl font-semibold text-navy-700 leading-[1.05] tracking-tight">
                                Une entreprise locale, fiable et réactive.
                            </h2>
                        </Reveal>
                    </div>
                    <div className="md:col-span-7">
                        <Reveal delay={0.1}>
                            <p className="text-navy-700/80 text-base md:text-lg leading-[1.75]">
                                Basée à Plan-les-Ouates, RCPS Terrassement Sàrl intervient à Genève, dans l’ensemble
                                du canton de Genève et dans toute la Suisse romande. Nous accompagnons les
                                particuliers, architectes, régies, promoteurs, entreprises générales et collectivités
                                dans leurs travaux de terrain, de démolition, d’infrastructure et d’aménagement
                                extérieur.
                            </p>
                            <p className="mt-5 text-navy-700/80 text-base md:text-lg leading-[1.75]">
                                Notre approche repose sur une analyse concrète du chantier : localisation, accès,
                                nature du sol, volumes à évacuer, réseaux existants, contraintes de voisinage, délais,
                                sécurité et autorisations éventuelles. Chaque demande est étudiée selon les contraintes
                                techniques et logistiques du projet.
                            </p>
                            <div className="mt-8 grid sm:grid-cols-3 gap-4">
                                <FeatureLine icon={<ShieldCheck className="w-4 h-4" />}>Analyse du chantier</FeatureLine>
                                <FeatureLine icon={<Clock className="w-4 h-4" />}>Sécurité prise en compte</FeatureLine>
                                <FeatureLine icon={<MapPin className="w-4 h-4" />}>Ancrage genevois</FeatureLine>
                            </div>
                            <Link
                                to="/entreprise"
                                data-testid="home-about-link"
                                className="mt-8 inline-flex items-center gap-2 text-navy-700 font-semibold link-underline"
                            >
                                Notre approche <ArrowUpRight className="w-4 h-4" />
                            </Link>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Services */}
            <section className="bg-concrete-50 border-y border-concrete-200">
                <div className="rcps-container py-20 md:py-28">
                    <div className="grid md:grid-cols-12 gap-6 items-end mb-10">
                        <div className="md:col-span-7">
                            <Reveal>
                                <p className="kicker text-navy-700">
                                    <span className="hairline" /> Nos prestations
                                </p>
                                <h2 className="mt-4 font-heading text-3xl md:text-5xl font-semibold text-navy-700 tracking-tight">
                                    Quatre domaines d’intervention,
                                    <br className="hidden md:block" /> du terrain aux finitions.
                                </h2>
                            </Reveal>
                        </div>
                        <div className="md:col-span-5 md:justify-self-end">
                            <Reveal delay={0.1}>
                                <p className="text-navy-700/75 max-w-md">
                                    Terrassement, démolition, génie civil, voirie, enrobés, aménagements extérieurs
                                    et enrochement : RCPS Terrassement regroupe les prestations essentielles pour
                                    préparer, transformer, structurer et finaliser vos espaces extérieurs à Genève
                                    et en Suisse romande.
                                </p>
                            </Reveal>
                        </div>
                    </div>
                    <ServicesGrid layout="carousel" />
                </div>
            </section>

            {/* Notre approche — méthode en 4 étapes */}
            <section className="bg-white">
                <div className="rcps-container py-20 md:py-28">
                    <div className="grid md:grid-cols-12 gap-6 items-end mb-12 md:mb-16">
                        <div className="md:col-span-7">
                            <Reveal>
                                <p className="kicker text-navy-700">
                                    <span className="hairline" /> Notre approche
                                </p>
                                <h2 className="mt-4 font-heading text-3xl md:text-5xl font-semibold text-navy-700 tracking-tight">
                                    Une méthode claire,
                                    <br className="hidden md:block" /> de l’analyse à la réalisation.
                                </h2>
                            </Reveal>
                        </div>
                        <div className="md:col-span-5 md:justify-self-end">
                            <Reveal delay={0.1}>
                                <p className="text-navy-700/75 max-w-md leading-relaxed">
                                    Chaque chantier est préparé avec méthode afin d’anticiper les contraintes
                                    techniques, les accès, les matériaux, les évacuations, les délais et les
                                    éventuelles démarches nécessaires. Cette organisation permet un déroulement
                                    plus clair, plus sécurisé et mieux coordonné.
                                </p>
                            </Reveal>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {[
                            {
                                num: "01",
                                title: "Étude & devis",
                                desc: "Analyse de votre demande, de la localisation, des accès, du type de travaux et des contraintes principales du chantier.",
                                image: "/rcps-assets/notre-approche-01-etude.jpeg",
                                bullets: ["Analyse du projet", "Photos ou plans utiles", "Devis personnalisé"],
                            },
                            {
                                num: "02",
                                title: "Préparation du chantier",
                                desc: "Organisation des moyens, planification de l’intervention, prise en compte des accès, de la sécurité et des contraintes techniques.",
                                image: "https://customer-assets.emergentagent.com/job_elated-beaver-5/artifacts/6coeh1e9_Terrassement%20-%20Grande.jpeg",
                                bullets: ["Organisation des travaux", "Sécurité du site", "Coordination des étapes"],
                            },
                            {
                                num: "03",
                                title: "Réalisation des travaux",
                                desc: "Exécution des travaux de terrassement, démolition, génie civil, voirie, enrobés ou aménagement extérieur selon les prestations prévues.",
                                image: "/rcps-assets/notre-approche-03-realisation.jpg",
                                bullets: ["Machines adaptées", "Suivi du chantier", "Travaux soignés"],
                            },
                            {
                                num: "04",
                                title: "Contrôle & remise en état",
                                desc: "Vérification des prestations réalisées, remise en ordre de la zone d’intervention et échanges avec le client en fin de chantier.",
                                image: "/rcps-assets/notre-approche-04-reception.jpeg",
                                bullets: ["Contrôle final", "Zone remise en ordre", "Suivi client"],
                            },
                        ].map((step, i) => (
                            <Reveal key={step.num} delay={i * 0.08}>
                                <div className="group relative h-full bg-white rounded-md border border-concrete-200 overflow-hidden card-elevate flex flex-col">
                                    <div className="relative aspect-[4/3] overflow-hidden">
                                        <img
                                            src={step.image}
                                            alt={step.title}
                                            loading="lazy"
                                            className="w-full h-full object-cover transition duration-[900ms] group-hover:scale-105"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-navy-900/50 via-transparent to-transparent" />
                                        <div className="absolute top-3 left-3 bg-white/95 backdrop-blur px-2.5 py-1 rounded font-mono-num text-[11px] text-navy-700 font-semibold">
                                            Étape {step.num}
                                        </div>
                                    </div>
                                    <div className="p-6 flex flex-col flex-1">
                                        <div className="flex items-baseline gap-3">
                                            <span className="big-num text-navy-700/15 text-3xl">{step.num}</span>
                                            <h3 className="font-heading text-lg md:text-xl font-semibold text-navy-700 tracking-tight">
                                                {step.title}
                                            </h3>
                                        </div>
                                        <p className="mt-3 text-sm text-navy-700/75 leading-relaxed">
                                            {step.desc}
                                        </p>
                                        <ul className="mt-5 space-y-2 pt-4 border-t border-concrete-100">
                                            {step.bullets.map((b) => (
                                                <li
                                                    key={b}
                                                    className="flex items-center gap-2 text-xs text-navy-700/80"
                                                >
                                                    <span className="w-1 h-1 rounded-full bg-navy-700/50" />
                                                    {b}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why RCPS */}
            <section className="relative bg-navy-700 text-white overflow-hidden">
                <div className="absolute inset-0 bg-topo-dark opacity-70 pointer-events-none" />
                <div className="absolute inset-0 noise-overlay pointer-events-none" />
                <div className="relative rcps-container py-20 md:py-28">
                    <div className="grid md:grid-cols-12 gap-10 items-end mb-12">
                        <div className="md:col-span-7">
                            <Reveal>
                                <p className="kicker text-white/70">
                                    <span className="hairline bg-white/60" /> Pourquoi RCPS Terrassement ?
                                </p>
                                <h2 className="mt-4 font-heading text-3xl md:text-5xl font-semibold tracking-tight">
                                    Le sérieux d’une entreprise suisse,
                                    <br className="hidden md:block" /> l’exigence d’un partenaire de long terme.
                                </h2>
                            </Reveal>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                        {REASSURANCE.map((r, i) => (
                            <Reveal key={r.title} delay={i * 0.05}>
                                <div className="h-full rounded-md border border-white/12 bg-white/[0.03] p-6 card-elevate">
                                    <div className="flex items-center gap-3">
                                        <span className="font-mono-num text-white/60 text-xs">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        <span className="h-px w-8 bg-white/25" />
                                    </div>
                                    <h3 className="mt-5 font-heading text-lg md:text-xl font-semibold tracking-tight">
                                        {r.title}
                                    </h3>
                                    <p className="mt-2 text-white/70 text-sm leading-relaxed">{r.desc}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Réalisations teaser */}
            <section className="bg-white">
                <div className="rcps-container py-20 md:py-28">
                    <div className="grid md:grid-cols-12 gap-6 items-end mb-10">
                        <div className="md:col-span-7">
                            <Reveal>
                                <p className="kicker text-navy-700">
                                    <span className="hairline" /> Réalisations
                                </p>
                                <h2 className="mt-4 font-heading text-3xl md:text-5xl font-semibold text-navy-700 tracking-tight">
                                    Exemples de réalisations
                                    <br className="hidden md:block" /> et typologies de chantiers.
                                </h2>
                            </Reveal>
                        </div>
                        <div className="md:col-span-5 md:justify-self-end">
                            <Reveal delay={0.1}>
                                <Link
                                    to="/realisations"
                                    className="inline-flex items-center gap-2 text-navy-700 font-semibold link-underline"
                                >
                                    Voir toutes les réalisations <ArrowUpRight className="w-4 h-4" />
                                </Link>
                            </Reveal>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-5">
                        {PROJECTS.slice(0, 3).map((p, i) => (
                            <Reveal key={p.id} delay={i * 0.05}>
                                <Link to="/realisations" className="group block relative overflow-hidden rounded-md border border-concrete-200">
                                    <div className="aspect-[4/5] overflow-hidden">
                                        <img
                                            src={p.image}
                                            alt={p.title}
                                            className="w-full h-full object-cover transition duration-700 group-hover:scale-105"
                                        />
                                    </div>
                                    <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/85 via-black/30 to-transparent text-white">
                                        <p className="text-[11px] uppercase tracking-[0.16em] text-white/70">{p.category} · {p.location}</p>
                                        <h3 className="mt-1 font-heading text-lg font-semibold leading-snug">{p.title}</h3>
                                    </div>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <InlineCTA
                variant="navy"
                title="Un chantier en tête ? Parlez-nous de votre projet."
                text="Terrassement, démolition, génie civil, voirie, enrobés, aménagements extérieurs ou enrochement à Genève et en Suisse romande."
                primary={{ label: "Demander un devis", to: "/contact" }}
                secondary={{ label: "À propos de nous", to: "/entreprise" }}
                dataTestId="home-inline-cta"
            />

            {/* Contact rapide */}
            <section className="bg-concrete-50 border-t border-concrete-200">
                <div className="rcps-container py-20 md:py-28 grid md:grid-cols-12 gap-10 items-start">
                    <div className="md:col-span-5">
                        <Reveal>
                            <p className="kicker text-navy-700">
                                <span className="hairline" /> Contact rapide
                            </p>
                            <h2 className="mt-4 font-heading text-3xl md:text-5xl font-semibold text-navy-700 leading-tight tracking-tight">
                                Décrivez votre projet
                                <br />
                                en quelques lignes.
                            </h2>
                            <p className="mt-6 text-navy-700/75 leading-relaxed">
                                Vous avez un projet de terrassement, démolition, génie civil, voirie, enrobés,
                                aménagement extérieur ou enrochement à Genève ou en Suisse romande ?
                            </p>
                            <p className="mt-4 text-navy-700/75 leading-relaxed">
                                Transmettez-nous les informations principales du chantier. RCPS Terrassement vous
                                répondra afin d’analyser votre demande, la localisation du chantier, les accès, la
                                nature des travaux et les éventuelles contraintes logistiques.
                            </p>
                            <ul className="mt-8 space-y-4">
                                <li className="flex items-start gap-3 text-navy-700">
                                    <Award className="w-5 h-5 text-steel" />
                                    <span className="text-sm">Devis personnalisé selon le chantier.</span>
                                </li>
                                <li className="flex items-start gap-3 text-navy-700">
                                    <Handshake className="w-5 h-5 text-steel" />
                                    <span className="text-sm">Intervention à Genève et dans toute la Suisse romande.</span>
                                </li>
                                <li className="flex items-start gap-3 text-navy-700">
                                    <Wrench className="w-5 h-5 text-steel" />
                                    <span className="text-sm">Solutions adaptées aux particuliers, professionnels, régies et collectivités.</span>
                                </li>
                            </ul>
                        </Reveal>
                    </div>
                    <div className="md:col-span-7">
                        <Reveal delay={0.1}>
                            <ContactForm compact />
                        </Reveal>
                    </div>
                </div>
            </section>

            <SectionCTA />
        </>
    );
}

function FeatureLine({ icon, children }) {
    return (
        <div className="flex items-center gap-3 border border-concrete-200 rounded-md p-3 bg-white">
            <span className="w-8 h-8 rounded bg-concrete-100 flex items-center justify-center text-navy-700">{icon}</span>
            <span className="text-sm font-medium text-navy-700">{children}</span>
        </div>
    );
}
